// TurboFulcrumExporter.js - Handles exporting to Turbo Fulcrum CSV format

/**
 * Maps Fulcrum field types to Turbo Fulcrum field types
 * @param {string} fulcrumType - Original Fulcrum field type
 * @returns {string} - Turbo Fulcrum field type
 */
const mapFieldType = (fulcrumType) => {
  // Direct mapping for most field types
  const typeMap = {
    'TextField': 'TextField',
    'NumericField': 'NumericField',
    'YesNoField': 'YesNoField',
    'DateTimeField': 'DateTimeField',
    'Label': 'Label',
    'TimeField': 'TimeField',
    'ChoiceField': 'ChoiceField',
    'MultiChoiceField': 'ChoiceField', // Map MultiChoiceField to ChoiceField in Turbo Fulcrum
    'AddressField': 'TextField', // Map AddressField to TextField
    'SignatureField': 'TextField', // Map SignatureField to TextField
    // Add more mappings as needed
  };
  
  return typeMap[fulcrumType] || 'TextField'; // Default to TextField if no mapping
};

/**
 * Convert choices array to comma-separated string format
 * @param {Array} choices - Array of choice objects
 * @returns {string} - Comma-separated choice values for Turbo Fulcrum
 */
const formatChoices = (choices) => {
  if (!choices || !Array.isArray(choices)) {
    return '';
  }
  
  return choices.map(choice => {
    // Handle both string choices and {label, value} format
    if (typeof choice === 'string') {
      return choice;
    }
    return choice.label || choice.value || '';
  }).join(', ');
};

/**
 * Converts form fields to Turbo Fulcrum CSV format
 * @param {Array} fields - The form fields
 * @returns {string} - CSV content
 */
const convertToTurboFulcrumCSV = (fields) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return '';
  }
  
  // Headers
  const csvRows = ['Type,Label,Description,Required,Disabled,Hidden,ChoiceValues'];
  
  fields.forEach(field => {
    const type = mapFieldType(field.type);
    const label = field.label || '';
    const description = field.description || '';
    const required = field.required ? 'TRUE' : 'FALSE';
    const disabled = field.disabled ? 'TRUE' : 'FALSE';
    const hidden = field.hidden ? 'TRUE' : 'FALSE';
    
    // Format choice values for ChoiceField
    let choiceValues = '';
    if (type === 'ChoiceField' && field.choices) {
      choiceValues = formatChoices(field.choices);
    }
    
    // Escape values that might contain commas by wrapping in quotes
    const escapedLabel = label.includes(',') ? `"${label}"` : label;
    const escapedDescription = description.includes(',') ? `"${description}"` : description;
    const escapedChoices = choiceValues ? `"${choiceValues}"` : '';
    
    // Build the CSV row
    const row = [
      type,
      escapedLabel,
      escapedDescription,
      required,
      disabled,
      hidden,
      escapedChoices
    ].join(',');
    
    csvRows.push(row);
  });
  
  return csvRows.join('\n');
};

/**
 * Downloads the Turbo Fulcrum CSV file
 * @param {Array} fields - The form fields
 * @param {string} formName - The form name (used for the filename)
 */
const downloadTurboFulcrumCSV = (fields, formName) => {
  const csvContent = convertToTurboFulcrumCSV(fields);
  
  // Create a blob and download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  // Create download link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${formName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_turbo_fulcrum.csv`);
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Export both functions
export { convertToTurboFulcrumCSV, downloadTurboFulcrumCSV };